import React from 'react';
import { Card, Row, Col } from 'antd';
import {
  TrophyOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { toJalali, formatCloseDateLabel } from '../../utils/dateUtils';

const formatLastDeal = (deal) => {
  if (!deal) return '—';
  if (typeof deal === 'string') return formatCloseDateLabel(deal) || toJalali(deal) || deal;
  const name = deal.name || deal.title;
  const date = deal.close_date || deal.closed_at || deal.created_at;
  const dateLabel = date ? (formatCloseDateLabel(date) || toJalali(date)) : null;
  const valueLabel = deal.value != null ? formatPriceToman(deal.value) : null;
  const parts = [name, valueLabel, dateLabel].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
};

const CustomerDealSummary = ({ customer }) => {
  const summary = customer?.deal_summary;
  if (!customer || !summary) return null;

  const items = [
    {
      key: 'won',
      label: 'معاملات موفق',
      value: summary.won_count ?? 0,
      icon: <TrophyOutlined className="text-green-600" />,
      valueClass: 'text-green-600',
      bg: 'from-green-50 to-white dark:from-green-900/20 dark:to-gray-800',
      border: 'border-green-100 dark:border-green-900/40',
    },
    {
      key: 'lost',
      label: 'معاملات ناموفق',
      value: summary.lost_count ?? 0,
      icon: <CloseCircleOutlined className="text-red-500" />,
      valueClass: 'text-red-500',
      bg: 'from-red-50 to-white dark:from-red-900/20 dark:to-gray-800',
      border: 'border-red-100 dark:border-red-900/40',
    },
    {
      key: 'total',
      label: 'جمع خرید تا الان',
      value: formatPriceToman(summary.total_purchase_amount || 0),
      icon: <DollarOutlined className="text-emerald-600" />,
      valueClass: 'text-gray-900 dark:text-gray-100',
      bg: 'from-emerald-50 to-white dark:from-emerald-900/20 dark:to-gray-800',
      border: 'border-emerald-100 dark:border-emerald-900/40',
      isText: true,
    },
    {
      key: 'last_won',
      label: 'آخرین معامله موفق',
      value: formatLastDeal(summary.last_successful_deal),
      icon: <CalendarOutlined className="text-blue-600" />,
      valueClass: 'text-gray-800 dark:text-gray-200',
      bg: 'from-blue-50 to-white dark:from-blue-900/20 dark:to-gray-800',
      border: 'border-blue-100 dark:border-blue-900/40',
      isText: true,
    },
  ];

  return (
    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-l from-slate-50/80 to-white dark:from-gray-900/40 dark:to-gray-900/20">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-0.5 h-4 bg-emerald-500 rounded-full" />
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 m-0 custom-font">
          خلاصه وضعیت مشتری
        </h3>
      </div>
      <Row gutter={[12, 12]}>
        {items.map((item) => (
          <Col key={item.key} xs={12} md={6}>
            <Card
              size="small"
              className={`h-full border shadow-sm bg-gradient-to-br ${item.bg} ${item.border}`}
              bodyStyle={{ padding: '12px 14px' }}
            >
              <div className="flex items-start gap-2">
                <div className="mt-0.5 shrink-0">{item.icon}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-1 custom-font">{item.label}</div>
                  <div
                    className={`font-bold custom-font truncate ${item.isText ? 'text-sm' : 'text-xl'} ${item.valueClass}`}
                    title={String(item.value)}
                  >
                    {item.value}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default CustomerDealSummary;
